import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { type SelectOption } from '@framework/form/inputs/SelectInput.tsx';
import { ISODate } from '@framework/ui/components/date/ISODate.tsx';
import { Space } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { useCommunicationsContext } from '../../context/CommunicationsContext.tsx';
import { CommunicationReadStateTag } from '../../detail/CommunicationReadStateTag.tsx';
import { CommunicationRecipientsView } from '../../detail/CommunicationRecipientsView.tsx';
import { CommunicationSenderView } from '../../detail/CommunicationSenderView.tsx';
import { CommunicationStateTag } from '../../detail/CommunicationStateTag.tsx';
import { getIsCommunicationCurrentUser } from '../../getIsCommunicationCurrentUser.tsx';
import { CommunicationLogic } from '../../logic/CommunicationLogic.ts';

export function useCommunicationColumns(): ColumnsType<ComunicazioneDTO> {
  const { procedureId, isBackoffice } = useCommunicationsContext();
  const user = useCurrentUser();

  const result = useMemo(() => {
    return [
      {
        title: 'Stato',
        key: 'stato',
        dataIndex: 'stato',
        render: (_, record) => {
          // Lo stato di lettura riguarda solo le comunicazioni ricevute
          const isReceived =
            !CommunicationLogic.isDraft(record) &&
            !getIsCommunicationCurrentUser(
              CommunicationLogic.sender(record)?.id,
              user?.utenteLoggato,
            );

          return (
            <Space align="center">
              <CommunicationStateTag communication={record} />
              {isReceived && (
                <CommunicationReadStateTag
                  recipient={CommunicationLogic.currentRecipient(record, {
                    isBackoffice,
                    procedureId,
                  })}
                />
              )}
            </Space>
          );
        },
      },
      {
        title: 'Data',
        key: 'dataInvio',
        dataIndex: 'dataInvio',
        render: (_, record) => (
          <ISODate date={record.dataInvio} format="DD/MM/YYYY HH:mm" />
        ),
      },
      {
        title: 'Mittente',
        key: 'mittente',
        dataIndex: 'mittente',
        render: (_, record) => (
          <b>
            <CommunicationSenderView communication={record} compactView />
          </b>
        ),
      },
      {
        title: 'Destinatari',
        key: 'destinatari',
        dataIndex: 'destinatari',
        render: (_, record) => (
          <CommunicationRecipientsView communication={record} compactView />
        ),
      },

      {
        title: 'Tipo Comunicazione',
        key: 'tipoComunicazione.codice',
        dataIndex: 'tipoComunicazione.codice',
        render: (_, record) => record.tipoComunicazione?.codice,
      },
      {
        title: 'Oggetto',
        key: 'oggetto',
        dataIndex: 'oggetto',
        render: (_, record) => <b>{record.oggetto}</b>,
      },
    ] satisfies ColumnsType<ComunicazioneDTO>;
  }, [isBackoffice, procedureId, user?.utenteLoggato]);

  return result;
}

/**
 * Lista dei campi di ordinamento per le comunicazioni
 */
export const communicationSortingFields: SelectOption[] = [
  { value: 'dataModifica', label: 'Data modifica' },
  { value: 'mittente', label: 'Mittente' },
  { value: 'destinatario', label: 'Destinatario' },
  { value: 'oggetto', label: 'Oggetto' },
  { value: 'dataInvio', label: 'Data invio' },
  { value: 'dataCreazione', label: 'Data creazione' },
  { value: 'tipoComunicazione', label: 'Tipo comunicazione' },
].sort((a, b) => a.label.localeCompare(b.label));
